import type { StorageUsage } from '@ralph/contracts'
import type { I18n } from '@/i18n/index.ts'

/**
 * The pure half of the storage indicator.
 *
 * Everything here turns measured bytes into the two things a gauge needs — how full, and how
 * worried to look — and nothing here invents a denominator. That restraint is the whole design:
 * three of the four stores on this screen have no platform-wide limit at all (documents are
 * capped per base, the vector index is capped by nothing, the database grows until the volume
 * says no), so a bar drawn against a made-up ceiling would be the most confident lie on the
 * page. A store without a real limit gets `fill: null` and renders as a figure, not a gauge.
 *
 * ── Where the formatting comes from ──
 *
 * Nothing here formats a number on its own. Every function that renders one takes the active
 * locale's bound formatters as a parameter, because importing them at module scope would
 * freeze the language at import time and keep rendering the previous one after a switch, with
 * no type error, no runtime error and no failing test (PRD-27 F-3). The caller is a component
 * and already holds `useI18n()`; it threads what it has.
 *
 * This screen's own `formatSize` is gone. It existed because the shared `formatBytes` stopped
 * at MB and left a trailing ".0"; `i18n/format.ts` now reaches TB and drops the zero the same
 * way, so the two had become one function with one of them rounding in US notation. Its cases
 * survive in this module's spec as the fence proving the fold moved no rendering.
 *
 * ── And the nouns are gone too ──
 *
 * `fileCount`, `passageCount`, `baseCount`, `knowledgeBaseCount` and `removedDocumentsAre`
 * lived here as English literals behind a generic pluraliser. They are COPY, so they are now
 * `t.activity.storage.*` with every other sentence this screen says (PRD-27 LOC-010,
 * LOC-017). What is left is what a locale must never be able to change: a threshold, a
 * ratio, a rank and a clamp.
 */

/** Above this share of a limit, a store is worth noticing. */
export const PRESSURE_WARM = 0.75

/** Above this, it is worth acting on before the next upload is refused. */
export const PRESSURE_HOT = 0.9

export type Pressure = 'calm' | 'warm' | 'hot'

/**
 * A used-against-limit reading, in whatever unit the caller is counting.
 *
 * Unit-neutral on purpose: the same shape measures bytes against an allowance and files against
 * a count, and a maker's knowledge base is metered on both at once. Naming the fields for bytes
 * would have made the second use read like a bug.
 */
export type Capacity = {
  used: number
  limit: number | null
  /** 0…1, or null when there is no limit to be a fraction of. */
  fill: number | null
  pressure: Pressure
}

export const pressureOf = (fill: number | null): Pressure => {
  if (fill === null) return 'calm'
  if (fill >= PRESSURE_HOT) return 'hot'
  if (fill >= PRESSURE_WARM) return 'warm'
  return 'calm'
}

/**
 * A used-against-limit reading.
 *
 * Clamped at 1 rather than allowed to overflow: a base can exceed its allowance (an
 * administrator can lower a quota under material that is already there), and a bar drawn at
 * 140% would be a rendering bug on top of a real problem. The number beside it still says the
 * true figure, and the pressure is already `hot`.
 *
 * A zero or negative limit is treated as no limit at all. It cannot arrive from the contract,
 * which requires a positive integer — but division is not the place to find out.
 */
export const capacity = (used: number, limit: number | null): Capacity => {
  const usable = limit !== null && limit > 0 ? limit : null
  const fill = usable === null ? null : Math.min(1, Math.max(0, used / usable))
  return { used, limit: usable, fill, pressure: pressureOf(fill) }
}

/**
 * What share of the database the vector index accounts for.
 *
 * The interesting ratio on a knowledge platform, because it is the one that grows without
 * anybody uploading anything: re-chunking or re-embedding an unchanged corpus moves this number
 * and moves nothing else on the screen. Null when the database size could not be read, so the
 * card says "unknown" rather than "0%".
 */
export const vectorShare = (storage: StorageUsage): number | null =>
  storage.database.totalBytes > 0 ? Math.min(1, storage.vectors.totalBytes / storage.database.totalBytes) : null

export type VolumeReading = { usedBytes: number; totalBytes: number; freeBytes: number; fill: number }

/**
 * The artifact volume, as a used-against-total reading.
 *
 * Used is derived from total minus available, NOT from the sum of artifact sizes: everything
 * else on the disk — the database's own data directory on a single-node deployment, logs,
 * container images — is what actually fills it, and a gauge that only counted our own bytes
 * would read 2% while the volume was full.
 */
export const volumeReading = (volume: StorageUsage['artifacts']['volume']): VolumeReading | null => {
  if (volume === null || volume.totalBytes <= 0) return null
  const freeBytes = Math.min(volume.freeBytes, volume.totalBytes)
  const usedBytes = volume.totalBytes - freeBytes
  return { usedBytes, totalBytes: volume.totalBytes, freeBytes, fill: usedBytes / volume.totalBytes }
}

export type FullestBase = {
  sourceId: string
  /** Null when the reader cannot use the base (PRD-26 D-54): the gauge names its owner instead. */
  name: string | null
  ownerName: string
  ownerKind: 'user' | 'group'
  capacity: Capacity
}

/**
 * The base closest to its storage limit.
 *
 * Every base an upload can be refused on carries its own limit (PRD-26 D-41) — a person's the
 * personal allowance, a group's the larger group one — and only an administrator's own base has
 * none. Measuring each against ITS limit is what keeps the gauge honest: one allowance for all of
 * them would call a group base "full" at a size its owners may still add to.
 *
 * Returns the fullest by SHARE rather than by size: the point is which one is about to refuse an
 * upload, and the largest base is usually not it.
 */
export const fullestLimitedBase = (storage: StorageUsage): FullestBase | null =>
  storage.bySource
    .filter((source) => source.storageLimitBytes !== null)
    .map((source) => ({
      sourceId: source.sourceId,
      name: source.name,
      ownerName: source.ownerName,
      ownerKind: source.ownerKind,
      capacity: capacity(source.bytes, source.storageLimitBytes)
    }))
    .sort((a, b) => (b.capacity.fill ?? 0) - (a.capacity.fill ?? 0))[0] ?? null

/**
 * Bases ranked by what they occupy, with each one's share of the total.
 *
 * The server already returns them largest-first and capped; this adds the share and the
 * per-base pressure so the table can be read without doing arithmetic in one's head. `share`
 * is against the total of what came back rather than against `documents.bytes`, so the bars on
 * screen always add up to the rows on screen.
 */
export type SourceRow = StorageUsage['bySource'][number] & { share: number; capacity: Capacity }

export const rankedSources = (storage: StorageUsage, limit = 8): SourceRow[] => {
  const shown = storage.bySource.slice(0, limit)
  const total = shown.reduce((sum, source) => sum + source.bytes, 0)

  return shown.map((source) => ({
    ...source,
    share: total > 0 ? source.bytes / total : 0,
    capacity: capacity(source.bytes, source.storageLimitBytes)
  }))
}

/**
 * What a presentation function here needs from the active locale.
 *
 * A `Pick` rather than the whole `I18n` so the signature states what each one actually reads,
 * and a parameter rather than an import for the reason in the header: a captured formatter is
 * a captured language.
 */
export type Formatting = Pick<I18n, 'locale' | 'formatCount' | 'formatPercent'>

/**
 * A share as a percentage, with the same rule the rest of the product uses for rates: a real
 * value never rounds away to nothing, because "0%" of a store that is holding something reads
 * as broken.
 *
 * The `<` is ours — it is a threshold marker, not a number — and the 1% behind it still goes
 * through `Intl`, so the whole string localizes even though only part of it is a figure.
 * There is no word in it, which is why it stayed here when the nouns left.
 */
export const formatShare = (fmt: Formatting, share: number | null): string | null => {
  if (share === null || !Number.isFinite(share)) return null
  const clamped = Math.min(1, Math.max(0, share))
  if (clamped > 0 && clamped < 0.01) return `<${fmt.formatPercent(0.01, 0)}`
  return fmt.formatPercent(clamped, 0)
}
